import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, CheckCircle2, XCircle, Database, HardDrive, Activity } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { formatDateTime } from '../../utils/datetime';

const fmtInt = (n) => (n ?? 0).toLocaleString();
const fmtMB = (n) => `${(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} MB`;

const HealthPanel = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/platform/health');
      setHealth(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  if (loading && !health) {
    return <div className="p-8 text-center text-[13px] text-slate-500">Loading health…</div>;
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 text-[13px] rounded-lg px-4 py-3">{error}</div>
    );
  }
  if (!health) return null;

  const mongoOk = health.mongo?.ok;
  const dbStats = health.db_stats || {};
  const counts = health.collection_counts || {};

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900 dark:text-slate-100">System health</h2>
          <p className="text-[12px] text-slate-500 dark:text-slate-400">
            Snapshot as of {health.timestamp ? formatDateTime(health.timestamp) : '—'}
          </p>
        </div>
        <button
          onClick={fetchHealth}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 p-4">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-slate-500" />
            <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">MongoDB ping</p>
          </div>
          <div className="mt-2 flex items-center gap-2">
            {mongoOk ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                <span className="text-[15px] font-semibold text-emerald-700">Reachable</span>
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5 text-rose-500" />
                <span className="text-[15px] font-semibold text-rose-700">Down</span>
              </>
            )}
          </div>
          {health.mongo?.error && (
            <p className="mt-1 text-[11px] text-rose-500">{health.mongo.error}</p>
          )}
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 p-4">
          <div className="flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-slate-500" />
            <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Data size</p>
          </div>
          <p className="mt-2 text-[20px] font-semibold text-slate-900 dark:text-slate-100">{fmtMB(dbStats.data_size_mb)}</p>
          <p className="text-[11px] text-slate-400 dark:text-slate-500">Storage {fmtMB(dbStats.storage_size_mb)}</p>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 p-4">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-slate-500" />
            <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Indexes</p>
          </div>
          <p className="mt-2 text-[20px] font-semibold text-slate-900 dark:text-slate-100">{fmtInt(dbStats.indexes)}</p>
          <p className="text-[11px] text-slate-400 dark:text-slate-500">{fmtMB(dbStats.index_size_mb)} total</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 p-4">
          <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">DB overview</p>
          <dl className="grid grid-cols-2 gap-y-2 text-[13px]">
            <dt className="text-slate-500 dark:text-slate-400">Collections</dt>
            <dd className="text-slate-900 dark:text-slate-100 font-semibold text-right">{fmtInt(dbStats.collections)}</dd>
            <dt className="text-slate-500 dark:text-slate-400">Documents</dt>
            <dd className="text-slate-900 dark:text-slate-100 font-semibold text-right">{fmtInt(dbStats.objects)}</dd>
          </dl>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 p-4">
          <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Collection counts</p>
          <div className="max-h-64 overflow-y-auto">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {Object.entries(counts).map(([name, count]) => (
                    <tr key={name}>
                      <td className="py-1.5 text-slate-600 dark:text-slate-400">{name}</td>
                      <td className="py-1.5 text-right text-slate-900 dark:text-slate-100 font-medium">{fmtInt(count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthPanel;
